"use client";

import { Suspense, useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { HomeIcon } from "lucide-react";
import { offices, orderByOptions } from "@/lib/utils/Offices";
import Link from "next/link";
import { useDictionaryStore } from "@/lib/store/dictionaryStore";
import { getCategoryDisplayName } from "@/lib/utils/categoryHelpers";

const BRAND_NAMES_MAP: Record<string, string> = {
  "599": "TIANDY",
  "149": "AVIGILON",
  "110": "VIVOTEK",
  "454": "BOLIDE",
  "124": "PELCO",
  "104": "HIKVISION",
  "101": "ARECONT",
  "129": "DAHUA",
  "562": "UNIARCH",
  "545": "AVIGILON ALTA",
  "106": "LONGSE",
  "130": "EXACQ",
  "354": "ALARM",
  "491": "INTRUSION",
  "504": "SAFR",
  "107": "NVT PHYBRIDGE",
  "108": "ECORP",
  "113": "WESTERN DIGITAL",
  "197": "DAHUA.",
  "237": "HiLook by HIKVISION",
  "355": "PROMOCION",
};

function AutoBreadcrumbContent() {
  const searchParams = useSearchParams();
  const { categoriesMenu } = useDictionaryStore();
  const [brandsMap, setBrandsMap] =
    useState<Record<string, string>>(BRAND_NAMES_MAP);

  useEffect(() => {
    interface BrandEntry {
      marcaCode?: string | number;
      code?: string | number;
      marcaName?: string;
      name?: string;
    }
    fetch("/api/business-operations/brands")
      .then((res) => res.json())
      .then((res: BrandEntry[] | { data?: BrandEntry[] }) => {
        const brandsList = Array.isArray(res) ? res : res?.data || [];
        const newMap = { ...BRAND_NAMES_MAP };
        brandsList.forEach((b) => {
          const code = String(b.marcaCode || b.code || "");
          const name = b.marcaName || b.name || "";
          if (code && name) {
            newMap[code] = name;
          }
        });
        setBrandsMap(newMap);
      })
      .catch(() => {});
  }, []);

  const entries = Array.from(searchParams.entries());
  if (entries.length === 0) {
    return null;
  }

  function createRedirect(
    text: string | string[],
    ref: string,
    index: number,
    rawValues?: (string | number)[] | string | number,
  ) {
    if (Array.isArray(text)) {
      const rawArr = Array.isArray(rawValues) ? rawValues : text;
      return text.map((item, idx) => {
        return (
          <Link
            key={idx}
            className={
              idx === text.length - 1 && index === entries.length - 1
                ? "font-bold text-primary ml-1"
                : `hover:underline cursor-pointer hover:text-primary ${
                    idx !== 0 ? "ml-2" : ""
                  }`
            }
            href={
              "/productos?" +
              (() => {
                const arr = rawArr.slice(0, idx + 1);
                const params = new URLSearchParams(searchParams.toString());
                params.set(ref, JSON.stringify(arr));
                entries.forEach(([key], i) => {
                  if (i > index) params.delete(key);
                });
                return params.toString();
              })()
            }
          >
            / {item.charAt(0).toUpperCase() + item.slice(1)}
          </Link>
        );
      });
    }

    const rawVal = rawValues !== undefined ? rawValues : text;

    return (
      <Link
        className={
          entries.length - 1 === index
            ? "font-bold text-primary"
            : "hover:underline cursor-pointer"
        }
        href={
          "/productos?" +
          (() => {
            const params = new URLSearchParams();
            entries.forEach(([key, value], i) => {
              if (i < index) params.set(key, value);
              if (i === index) params.set(key, String(rawVal));
            });
            return params.toString();
          })()
        }
      >
        {`/ ${text}`}
      </Link>
    );
  }

  function generateBreadcrumbLabel(key: string, value: string, index: number) {
    switch (key) {
      case "query":
        return createRedirect(value, key, index, value);
      case "sort":
        const position = orderByOptions.findIndex(
          (option) => option.value === value,
        );
        return createRedirect(
          position !== -1 ? orderByOptions[position].label : value,
          key,
          index,
          value,
        );
      case "inStockWarehouse05Only":
        return createRedirect("En liquidación", key, index, value);
      case "marcaCodes":
        try {
          const brandCodes = JSON.parse(value);
          const parsedCodes: (string | number)[] = Array.isArray(brandCodes)
            ? brandCodes
            : [brandCodes];
          const rawCodes = parsedCodes.map((code) => {
            const num = Number(code);
            return !isNaN(num) ? num : code;
          });
          const mappedBrandNames = parsedCodes.map(
            (code) => brandsMap[String(code)] || String(code),
          );
          return createRedirect(mappedBrandNames || "", key, index, rawCodes);
        } catch {
          return createRedirect(value || "", key, index, value);
        }
      case "inStockTotalOnly":
        return createRedirect("En existencia", key, index, value);
      case "isKit":
        return createRedirect("Kits", key, index, value);
      case "isFeatured":
        return createRedirect("Destacado", key, index, value);
      case "isNew":
        return createRedirect("Nuevo", key, index, value);
      case "office":
        const officeName =
          offices.find((office) => office.value === value)?.label || "";
        return createRedirect(officeName, key, index, value);
      case "tags":
        try {
          const arrayKeys = JSON.parse(value);
          const parsedTags = Array.isArray(arrayKeys) ? arrayKeys : [arrayKeys];
          return createRedirect(parsedTags || "", key, index, parsedTags);
        } catch {
          return createRedirect(value || "", key, index, value);
        }
      case "categoryPath": {
        const segments = value.split("/").filter(Boolean);
        return segments.map((slug, idx) => {
          const currentPath = segments.slice(0, idx + 1).join("/");
          const displayName = getCategoryDisplayName(
            categoriesMenu,
            currentPath,
            slug,
          );
          const isLast =
            idx === segments.length - 1 && index === entries.length - 1;

          return (
            <Link
              key={idx}
              className={
                isLast
                  ? "font-bold text-primary ml-1"
                  : `hover:underline cursor-pointer hover:text-primary ${
                      idx !== 0 ? "ml-2" : ""
                    }`
              }
              href={
                "/productos?" +
                (() => {
                  const newParams = new URLSearchParams(
                    searchParams.toString(),
                  );
                  newParams.set("categoryPath", currentPath);
                  entries.forEach(([k], i) => {
                    if (i > index) newParams.delete(k);
                  });
                  return newParams.toString();
                })()
              }
            >
              / {displayName}
            </Link>
          );
        });
      }
      default:
        return "";
    }
  }

  return (
    <div className="bg-white px-3.5 py-1">
      <nav className="flex items-center text-sm" aria-label="Breadcrumb">
        <div className="gap-1 flex flex-wrap items-center">
          <Link
            href="/"
            className="flex hover:underline cursor-pointer hover:text-primary"
          >
            <HomeIcon className="inline-block w-4 h-4 mb-1" />
          </Link>
          <Link
            href="/"
            className="flex hover:underline cursor-pointer hover:text-primary"
          >
            Inicio
          </Link>
          {entries.map(([key, value], index) => (
            <div key={`${key}-${index}`}>
              {generateBreadcrumbLabel(key, value, index)}
            </div>
          ))}
        </div>
      </nav>
    </div>
  );
}

export default function AutoBreadcrumb() {
  return (
    <Suspense fallback={null}>
      <AutoBreadcrumbContent />
    </Suspense>
  );
}
